import React, { useState, useEffect } from 'react';
import { Fade, Slide, Grow, Box } from '@mui/material';

const SmoothTransition = ({ 
  children, 
  type = 'fade', 
  delay = 0, 
  duration = 300,
  direction = 'up',
  threshold = 0.1,
  rootMargin = '50px'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (isVisible && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isVisible, hasAnimated]);

  const getTransitionProps = () => {
    const baseProps = {
      in: isVisible,
      timeout: duration,
    };

    switch (type) {
      case 'fade':
        return { ...baseProps };
      case 'slide':
        return { 
          ...baseProps, 
          direction: direction,
          mountOnEnter: true,
          unmountOnExit: true
        };
      case 'grow':
        return { 
          ...baseProps,
          mountOnEnter: true,
          unmountOnExit: true
        };
      default:
        return baseProps;
    }
  };

  const renderTransition = () => {
    const props = getTransitionProps();

    switch (type) {
      case 'fade':
        return (
          <Fade {...props}>
            <Box>{children}</Box>
          </Fade>
        );
      case 'slide':
        return (
          <Slide {...props}>
            <Box>{children}</Box>
          </Slide>
        );
      case 'grow':
        return (
          <Grow {...props}>
            <Box>{children}</Box>
          </Grow>
        );
      default:
        return children;
    }
  };

  return renderTransition();
};

// Staggered animation wrapper for lists
export const StaggeredList = ({ 
  children, 
  staggerDelay = 100,
  type = 'fade',
  direction = 'up'
}) => {
  return (
    <Box>
      {React.Children.map(children, (child, index) => (
        <SmoothTransition
          key={index}
          type={type}
          direction={direction}
          delay={index * staggerDelay}
          duration={300}
        >
          {child}
        </SmoothTransition>
      ))}
    </Box>
  );
};

// Page transition wrapper
export const PageTransition = ({ children, isVisible = true }) => {
  return (
    <SmoothTransition
      type="fade"
      delay={0}
      duration={500}
    >
      {isVisible ? children : null}
    </SmoothTransition>
  );
};

// Card transition wrapper
export const CardTransition = ({ children, index = 0 }) => {
  return (
    <SmoothTransition
      type="slide"
      direction="up"
      delay={index * 100}
      duration={400}
    >
      {children}
    </SmoothTransition>
  );
};

export default SmoothTransition;
